import { DateTime } from 'luxon';
import { assessmentQuesType } from '@containers/App/app.constant';
import { getCurrentDate } from '@utils/dateHelpers';
import { convertTimeToSeconds } from '@utils/helpers';
import formatString from '@utils/stringHelper';
import { every, filter, find, findIndex, first, flatMap, get, isEmpty, map, sortBy } from 'lodash';
export const processStartAssessmentResponse = (data) => {
    const { catalog_assessment, ...startAssessmentResponse } = data;
    const assessment = catalog_assessment?.assessment;
    const assessmentSections = assessment?.assessment_sections || [];
    const sortedAssessmentSections = sortBy(assessmentSections, 'sort_order');
    const firstSection = first(sortedAssessmentSections);
    return {
        assessment,
        assessmentSections,
        assessmentSection: firstSection,
        startAssessmentResponse,
    };
};
// canx_get_step_details.workflow_candidate_steps[] carries every candidate-step row on the
// shared step template; only one row belongs to this candidate. step_id is the match key.
export const getMatchedWorkflowCandidateStep = (step) => {
    const workflowCandidateSteps = step?.workflow_candidate_steps ?? [];
    return (workflowCandidateSteps.find((candidateStep) => candidateStep?.step_id === step?.id) ?? workflowCandidateSteps[0]);
};
// This candidate's workflow_candidate_step id for the step: the first row of workflow_candidate_steps.
// canx_get_step_details returns only this candidate's rows for this step, and it does not fill step_id.
export const getWorkflowCandidateStepId = (step) => step?.workflow_candidate_steps?.[0]?.id;
export const getLatestTestInstanceId = (workflowCandidateStep) => workflowCandidateStep?.test_instances?.[0]?.id;
// The pre-registration form is the step_form whose form.type is REGISTRATION (same rule as
// getStepDetailsWorker). Returns undefined while step details are unloaded or no such form exists —
// callers must not substitute another ID.
export const getPreFormStepFormId = (step) => step?.step_forms?.find((stepForm) => stepForm.form?.type === 'REGISTRATION')?.id;
// True when a section follows the one being submitted, i.e. the candidate moves on to another section
// rather than finishing the assessment. Sections run in sort_order (see processStartAssessmentResponse);
// the instance sections' is_completed is not reliably populated, so it is not used here.
export const hasRemainingSections = (assessmentSections, currentAssessmentSectionId) => {
    const currentSortOrder = find(assessmentSections, { id: currentAssessmentSectionId })?.sort_order;
    if (currentAssessmentSectionId === undefined || currentSortOrder === undefined) {
        return false;
    }
    return assessmentSections.some((section) => (section.sort_order ?? -Infinity) > currentSortOrder);
};
// accommodations[] lists every grant regardless of status (REQUESTED/PENDING_REVIEW/APPROVED/
// REJECTED) and includes non-extra-time accommodation types (duration_percentage: null). Only
// APPROVED extra-time grants count; take the max, never sum, matching the backend's own rule.
export const getApprovedDurationPercentage = (accommodations) => {
    const approvedPercentages = (accommodations ?? [])
        .filter((grant) => grant?.status === 'APPROVED' && grant?.duration_percentage != null)
        .map((grant) => grant.duration_percentage);
    return approvedPercentages.length > 0 ? Math.max(...approvedPercentages) : undefined;
};
// duration_percentage is sourced from canx_get_step_details ONLY — not from canx_start_assessment
// or canx_start_next_section, even though both also expose a duration_percentage field.
export const getStepDurationPercentage = (step) => getApprovedDurationPercentage(getMatchedWorkflowCandidateStep(step)?.accommodations);
// Backend contract (talview/supergraph#15127): round to the nearest whole minute (half rounds
// up), applied universally including 0%. Floored at 60s so a sub-30s duration doesn't round to 0,
// which downstream treats as "no limit".
export const applyDurationPercentage = (baseSeconds, durationPercentage) => {
    if (baseSeconds <= 0)
        return 0;
    const extendedSeconds = baseSeconds * (1 + (durationPercentage ?? 0) / 100);
    return Math.max(60, Math.round(extendedSeconds / 60) * 60);
};
// Same extend-then-round-to-whole-minute rule as applyDurationPercentage, for call sites that
// already compute their base duration in minutes (the instructions-page total-time helpers)
// rather than seconds — avoids a minutes -> seconds -> minutes round trip for no benefit.
// Floored at 1 min for the same reason applyDurationPercentage floors at 60s: a sub-30s total
// must not round to 0, which the instructions page renders as "-".
export const applyDurationPercentageToMinutes = (baseMinutes, durationPercentage) => {
    if (baseMinutes <= 0)
        return 0;
    return Math.max(1, Math.round(baseMinutes * (1 + (durationPercentage ?? 0) / 100)));
};
// Per-question async timers (preparation and answer) run in seconds and are not minute-rounded,
// so a short question keeps its exact length when there is no accommodation.
export const applyDurationPercentageToQuestionSeconds = (baseSeconds, durationPercentage) => (durationPercentage ? Math.round(baseSeconds * (1 + durationPercentage / 100)) : baseSeconds);
// The duration the section timer starts with on a fresh start: max_section_time when set, else
// answer_time for LONG_FORM. 0 means the section has no section-level timer. The instructions
// page derives its duration from this too, so the two always agree.
export const getSectionTimerSeconds = (section, durationPercentage) => {
    if (section?.max_section_time) {
        return applyDurationPercentage(convertTimeToSeconds(section.max_section_time) ?? 0, durationPercentage);
    }
    if (formatString(section?.section_type ?? '') === formatString(assessmentQuesType.LONG_FORM)) {
        return applyDurationPercentage(convertTimeToSeconds(section?.answer_time ?? '00:00:00') ?? 0, durationPercentage);
    }
    return 0;
};
export const processStartSectionResponse = (data, appState, step) => {
    const { questions, ...startSectionResponse } = data;
    let nextQuestionId;
    const appStateData = Number(appState?.section_id) === Number(startSectionResponse.id) ? appState?.state[0] : {};
    if (!appStateData?.nextQuestionId && !appStateData?.currentQuestionId) {
        nextQuestionId = first(questions);
    }
    else if (appStateData?.currentQuestionId) {
        nextQuestionId = appStateData?.currentQuestionId;
    }
    const assessment_section = startSectionResponse.assessment_section;
    // A persisted remaining_time of 0 means the previous section's timer had already
    // elapsed (e.g. auto-submit on timeout) — it is not a valid resume time for this
    // section, so fall back to computing the section's own max time instead.
    const remainingTime = appStateData?.remaining_time;
    const hasResumableTime = typeof remainingTime === 'number' && remainingTime > 0;
    const parsedMaxSectionTime = hasResumableTime
        ? remainingTime
        : getSectionTimerSeconds(assessment_section, getStepDurationPercentage(step));
    return {
        assessmentSection: assessment_section,
        next_question_id: nextQuestionId,
        startSectionResponse,
        questionsIds: questions,
        parsedMaxSectionTime,
        answers: appStateData?.answers || [],
    };
};
export const getNextQuestionId = (questionsIds, currentQuestionId) => {
    const currentQuestionIndex = findIndex(questionsIds || [], (question) => question === currentQuestionId);
    if (currentQuestionIndex >= 0 && currentQuestionIndex < questionsIds.length - 1) {
        return questionsIds[currentQuestionIndex + 1]; // Return next question id
    }
    return null;
};
export const getOverallSections = (data) => {
    const overallSections = filter(data, (item) => item?.section_configuration?.type === 'OVERALL');
    if (overallSections.length > 0)
        return overallSections;
    // Fallback: some backends return section_configuration.type = "SUB_VALUE" even when the single
    // row is actually the OVERALL measurement. Only fall back when EXACTLY ONE valid (non-null id,
    // has measurement data) row exists — that row is unambiguously the overall. With multiple rows
    // we cannot tell the true overall from an interim/partial sub-value, so return none and keep
    // polling rather than terminate on a partial score.
    const measurementRows = filter(data, (item) => item?.id != null && (item?.measurement_attributes?.length ?? 0) > 0);
    return measurementRows.length === 1 ? measurementRows : [];
};
export const postMessageToChild = (message) => {
    if (typeof window === 'undefined')
        return;
    const iframe = document.getElementById('code-solo-iframe');
    const targetWindow = iframe?.contentWindow;
    if (!targetWindow)
        return;
    let targetOrigin = '*';
    const src = iframe?.getAttribute('src');
    if (src) {
        try {
            targetOrigin = new URL(src).origin;
        }
        catch {
            targetOrigin = '*';
        }
    }
    targetWindow.postMessage(message, targetOrigin);
};
export const syncAppState = (appState) => {
    if (!appState?.state?.length)
        return;
    postMessageToChild({ type: 'APP_STATE', payload: appState.state[0] });
};
// The instance-level row (instance_id set, section_id null) carries the candidate's
// aggregated measurement_attributes across every section — total score is the sum of
// its OVERALL attrs, out-of value is the sum of its SUB_VALUE attrs' max_value.
export const getOverallNumericScore = (data) => {
    if (!data)
        return null;
    const instanceRow = find(data, (item) => item?.instance_id != null);
    if (!instanceRow)
        return null;
    const attributes = instanceRow.measurement_attributes ?? [];
    const overallAttrs = filter(attributes, (attr) => get(attr, 'section_configuration_attribute.type') === 'OVERALL' &&
        get(attr, 'section_configuration_attribute.attribute.attribute_type') === 'NUMERIC');
    if (overallAttrs.length === 0)
        return null;
    const totalScore = overallAttrs.reduce((sum, attr) => sum + (attr.numerical_score ?? 0), 0);
    const outOfValue = filter(attributes, (attr) => get(attr, 'section_configuration_attribute.type') === 'SUB_VALUE' &&
        get(attr, 'section_configuration_attribute.attribute.attribute_type') === 'NUMERIC').reduce((sum, attr) => sum + (get(attr, 'section_configuration_attribute.attribute.max_value') ?? 0), 0);
    return {
        ...overallAttrs[0],
        numerical_score: totalScore,
        section_configuration_attribute: {
            ...overallAttrs[0].section_configuration_attribute,
            attribute: {
                ...overallAttrs[0].section_configuration_attribute.attribute,
                max_value: outOfValue,
            },
        },
    };
};
export const getSkillScores = (data) => {
    if (!data)
        return null;
    const overallSections = getOverallSections(data);
    const skillScores = flatMap(overallSections, (section) => filter(section.measurement_attributes, (attr) => get(attr, 'section_configuration_attribute.type') === 'SUB_VALUE' &&
        get(attr, 'section_configuration_attribute.attribute.attribute_type') === 'NUMERIC'));
    return skillScores.length > 0 ? skillScores : null;
};
export const getResult = (data) => {
    if (!data)
        return null;
    const overallSections = getOverallSections(data);
    const result = flatMap(overallSections, (section) => filter(section.measurement_attributes, (attr) => get(attr, 'section_configuration_attribute.type') === 'OVERALL' &&
        get(attr, 'section_configuration_attribute.attribute.attribute_type') === 'ORDINAL'));
    return result.length > 0 ? get(result[0], 'ordinal.label', null) : null;
};
// Readiness gate for the score poll. The score is written in two stages: the section-level row lands
// first, then the instance-level OVERALL row carrying the ordinal label. Settling before the label
// exists shows the candidate the wrong verbiage (CX-5493), so poll on the label, not on row shape.
export const hasResultLabel = (data) => !isEmpty(getResult(data));
export const parseSubmittedAnswer = (payload, currentQuestion, startSectionResponse) => {
    let result;
    const selectedChoices = payload?.data?.choice;
    const questionChoices = currentQuestion?.question_choices;
    const formattedChoices = map(selectedChoices, (choice) => {
        const questionChoice = find(questionChoices, { id: choice });
        return {
            id: choice,
            is_correct: questionChoice?.is_correct,
        };
    });
    const hasQuestionChoices = Array.isArray(questionChoices) && questionChoices.length > 0;
    const hasTextResponse = !!payload?.data?.text_response?.doc;
    const hasCodeResponse = !!payload?.data?.code_response;
    if (hasQuestionChoices) {
        result = {
            data: {
                id: 'has_to_save',
                section_id: startSectionResponse.id,
                question_id: payload?.data?.question_id || currentQuestion.id,
                choice: selectedChoices,
                text_response: payload?.data?.text_response,
                code_response: payload?.data?.code_response,
                answered_at: getCurrentDate()?.toUTC().toISO(),
                is_correct_choice: every(formattedChoices, (choice) => typeof choice.is_correct === 'boolean')
                    ? every(formattedChoices, ['is_correct', true])
                    : null,
                is_answer_submitted: true,
            },
            success: true,
        };
    }
    else if (hasTextResponse || hasCodeResponse) {
        result = {
            data: {
                id: 'has_to_save',
                section_id: startSectionResponse.id,
                question_id: payload?.data?.question_id || currentQuestion.id,
                text_response: payload?.data?.text_response,
                code_response: payload?.data?.code_response,
                answered_at: getCurrentDate()?.toUTC().toISO(),
                is_answer_submitted: true,
            },
            success: true,
        };
    }
    else {
        result = {
            error_message: 'Error on save answer in state',
            data: null,
            success: false,
        };
    }
    return result;
};
export const parseBulkSavedAnswer = (response, allAnswers) => {
    const saveAnswerResponse = response.data.ae_save_answer;
    const errorResponse = filter(saveAnswerResponse, { success: false });
    const successResponse = filter(saveAnswerResponse, { success: true });
    const formattedAnswers = allAnswers.map((answer) => {
        const foundItem = successResponse.find((item) => item.data.question_id === answer.question_id);
        return foundItem ? foundItem.data : answer;
    });
    return {
        formattedAnswers: formattedAnswers || [],
        failedToSaveAnswer: errorResponse || [],
        successToSaveAnswer: successResponse || [],
    };
};
export const getCurrentQuestionIndex = (questionIds, currentQuestion) => {
    const index = findIndex(questionIds, (id) => id === currentQuestion?.id);
    return index + 1;
};
export const timestampToMinutes = (timestamp) => {
    // Parse the timestamp using Luxon
    if (timestamp) {
        const time = DateTime.fromFormat(timestamp, 'HH:mm:ss');
        // Extract hours and minutes from the time
        const hours = time.hour;
        const minutes = time.minute;
        const seconds = time.second;
        // Calculate total minutes
        const totalMinutes = hours * 60 + minutes + seconds / 60;
        return totalMinutes;
    }
    return 0;
};
// AE stores the async preparation and answer times on the section and applies them to every
// question. A question's own bank time applies only when the section leaves that time unset.
export const resolveAsyncQuestionTime = (sectionTime, questionTime) => timestampToMinutes(sectionTime) > 0 ? sectionTime : questionTime;
export const calculateTotalSectionTimeForAsync = (questions, section, returnRaw, durationPercentage) => {
    if (!questions || questions.length === 0)
        return returnRaw ? 0 : '-';
    const totalMinutes = questions.reduce((acc, item) => {
        const prepTime = timestampToMinutes(resolveAsyncQuestionTime(section?.preparation_time ?? null, item?.preparation_time ?? null));
        const answerTime = timestampToMinutes(resolveAsyncQuestionTime(section?.answer_time ?? null, item?.answer_time ?? null));
        return acc + prepTime + answerTime;
    }, 0);
    const extendedMinutes = applyDurationPercentageToMinutes(totalMinutes, durationPercentage);
    return returnRaw ? extendedMinutes : extendedMinutes ? `${extendedMinutes} mins` : '-';
};
export const formatAssessmentSectionType = (value) => value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
export const assessmentType = (assessmentSection) => {
    if (!assessmentSection) {
        return {
            isAssessmentTypeLongForm: false,
            isAssessmentTypeAsync: false,
            isAssessmentTypeObjective: false,
            isAssessmentTypeExternal: false,
            isAssessmentTypeIvy: false,
            isAssessmentTypeCoding: false,
        };
    }
    const extractAssessmentSectionType = formatString(get(assessmentSection, 'section_type') ?? '');
    const formattedAssessmentType = formatString(extractAssessmentSectionType ?? '');
    const isAssessmentTypeLongForm = formattedAssessmentType === formatString(assessmentQuesType.LONG_FORM);
    const isAssessmentTypeAsync = [assessmentQuesType.VIDEO, assessmentQuesType.ASYNC]
        .map((type) => formatString(type))
        .includes(formattedAssessmentType);
    const isAssessmentTypeObjective = assessmentQuesType.OBJECTIVE.map((type) => formatString(type)).includes(formattedAssessmentType);
    const isAssessmentTypeIvy = formattedAssessmentType === formatString(assessmentQuesType.IVY);
    const isAssessmentTypeExternal = formattedAssessmentType === formatString(assessmentQuesType.EXTERNAL) || isAssessmentTypeIvy;
    const isAssessmentTypeCoding = assessmentQuesType.CODING.map((type) => formatString(type)).includes(formattedAssessmentType);
    return {
        isAssessmentTypeLongForm,
        isAssessmentTypeAsync,
        isAssessmentTypeObjective,
        isAssessmentTypeExternal,
        isAssessmentTypeIvy,
        isAssessmentTypeCoding,
    };
};
export function getCentralizeSectionTime(sectionType, questions, assessmentSection, step) {
    const durationPercentage = getStepDurationPercentage(step);
    const timerMinutes = getSectionTimerSeconds(assessmentSection, durationPercentage) / 60;
    if (sectionType?.isAssessmentTypeObjective ||
        sectionType?.isAssessmentTypeExternal ||
        sectionType?.isAssessmentTypeCoding) {
        return timerMinutes;
    }
    else if (sectionType?.isAssessmentTypeAsync) {
        // Without max_section_time an async section has no section timer, only per-question timers.
        return (timerMinutes ||
            calculateTotalSectionTimeForAsync(questions, assessmentSection, true, durationPercentage));
    }
    else if (sectionType?.isAssessmentTypeLongForm) {
        return timerMinutes;
    }
    return 0;
}
export const ACTION_WINDOW_TYPE = 'ACTION';
export const findActionWindow = (step) => step?.step_windows?.find((w) => w?.window_type?.toUpperCase() === ACTION_WINDOW_TYPE);
